'use client';

import type { AxiosError } from '@cxnpl/api/axios';
import { useConfirmMobile, useRegisterUser, useVerifyMobile } from '@cxnpl/api/users/users';
import { Button, FormField, H1, SchemaForm, Text, useForm, YStack } from '@cxnpl/ui';
import branding from '@cxnpl/ui/brand';
import { useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import type { BRAND } from 'zod';
import { z } from 'zod';
import { logEvent, withTrackScreenView } from 'app/analytics';
import { PageBackNavigationFrame } from 'app/components/PageBackNavigationFrame';
import {
  selectAuthDetails,
  setCustomerNumber as setCustomerNumberSignup,
  setMobileNumber,
} from 'app/features/landing/signup/signupSlice';
import i18n from 'app/i18n/i18n.config';
import type { MfaFormProps } from 'app/provider/StepUpAuth/MfaForm';
import { MfaForm } from 'app/provider/StepUpAuth/MfaForm';
import { useAppDispatch, useAppSelector } from 'app/store';
import { appClient } from 'app/utils/appClient';
import { setCustomerNumber } from '../../authSlice';
import { createDeviceInfo } from '../../utils/createDeviceInfo';
import { useSetActiveStep } from '../SignupFormContext';

const MobileNumberFormSchema = z.object({
  mobileNumber: FormField.AustralianMobileNumber({
    required_error: `${i18n.t('auth.signUpPage.mobileNumberRequiredErrorMessage')}`,
    invalid_type_error: `${i18n.t('auth.signUpPage.mobileNumberInvalidError')}`,
  }).describe(`${i18n.t('auth.signUpPage.mobileNumberInputLabel')}`),
});

type MobileNumberFormFields = z.infer<typeof MobileNumberFormSchema>;

const MfaFormWithAnalytics = withTrackScreenView(MfaForm, {
  pageName: 'MobileVerification',
});

function MobileNumberForm({
  showMFAForm,
  setShowMFAForm,
}: {
  showMFAForm: boolean;
  setShowMFAForm: (value: boolean) => void;
}) {
  const { t } = useTranslation();
  const form = useForm(MobileNumberFormSchema);
  const dispatch = useAppDispatch();
  const mobile = useVerifyMobile();
  const confirmMobile = useConfirmMobile();
  const register = useRegisterUser();

  const { signupId, nickname, password, mobileNumber: savedMobileNumber } = useAppSelector(selectAuthDetails);
  const deviceInfo = createDeviceInfo(useAppSelector((state) => state.auth.deviceId) || '');

  const [isPending, startTransition] = useTransition();
  const setActiveStep = useSetActiveStep();

  const { isSubmitting: isFormSubmitting } = form.formState;
  const isSubmitting = isFormSubmitting || isPending;

  const onSubmit = async (values: MobileNumberFormFields) => {
    dispatch(setMobileNumber(values.mobileNumber));

    try {
      const response = await mobile.mutateAsync({
        data: {
          signupId,
          mobileNumber: values.mobileNumber,
        },
      });

      if ('signupRecordId' in response) {
        setShowMFAForm(true);
      }
    } catch (e) {
      const error = e as AxiosError;
      const errorMessage = error.response?.data.message
        ? error.response.data.message
        : t('auth.signUpPage.unknownError');

      form.setError('mobileNumber', { message: errorMessage, type: 'value' });
    }
  };

  const onSubmitMFA: MfaFormProps['onSubmit'] = async ({ code }, mfaForm) => {
    try {
      const response = await confirmMobile.mutateAsync({
        data: {
          signupId,
          code,
        },
      });

      // Handle invalid code or attempts exceeded
      if (!response.success) {
        let errorMessage;

        if (response.userIdAlreadyExists) {
          errorMessage = t('auth.components.mfaForm.mobileRegisteredMessage');
        } else if (response.attemptsExceeded) {
          errorMessage = t('auth.components.mfaForm.maxAttemptsMessage');
        } else {
          errorMessage = t('auth.components.mfaForm.invalidCodeResendMessage');
        }

        mfaForm.setValue('code', '' as string & BRAND<'passwordCode'>);
        mfaForm.setError('code', {
          message: errorMessage,
          type: 'value',
        });
        return;
      }

      const registerResponse = await register.mutateAsync({
        data: {
          signupId,
          nickname,
          password,
          appClient,
          deviceInfo,
        },
      });

      void logEvent('complete_signup');

      if ('customerNumber' in registerResponse) {
        // For rest of signup process
        dispatch(setCustomerNumberSignup(registerResponse.customerNumber || ''));
        // For rest of app and future logins
        if (appClient === 'MOBILE') {
          dispatch(setCustomerNumber(registerResponse.customerNumber || ''));
        }
      }

      // Transition breaks on mobile
      if (appClient === 'WEB') {
        startTransition(() => {
          setActiveStep('success');
        });
      } else {
        setActiveStep('success');
      }
    } catch (e) {
      const error = e as AxiosError;
      mfaForm.setValue('code', '' as string & BRAND<'passwordCode'>);
      mfaForm.setError('code', {
        message: error.response?.data.message || 'Invalid code',
        type: 'value',
      });
    }
  };
  const onResend: MfaFormProps['onResend'] = async (mfaForm) => {
    mfaForm.startCountdown(30);
    try {
      await mobile.mutateAsync({
        data: {
          signupId,
          mobileNumber: savedMobileNumber, // Using the saved mobile number over form.getValues('mobileNumber') as it has been transformed by the AustralianMobileNumberField
        },
      });
    } catch (e) {
      mfaForm.setError('code', {
        message: 'Failed to resend security code',
        type: 'value',
      });
    }
  };

  return (
    <YStack
      width="100%"
      $tablet={{
        maxWidth: 536,
      }}
      $laptop={{
        maxWidth: 468,
      }}
    >
      {showMFAForm ? (
        <MfaFormWithAnalytics
          title={t('auth.components.mfaForm.title')}
          description={t('auth.components.mfaForm.mobileDescription', { mobile: savedMobileNumber })}
          onSubmit={onSubmitMFA}
          onResend={onResend}
          disabled={isPending}
        />
      ) : (
        <YStack gap="$space.2xl" width="100%" flexDirection="column">
          <H1 textAlign="left" color="$foreground/surface-default">
            {t('auth.signUpPage.title', { productName: branding.productName })}
          </H1>
          <Text>{t('auth.signUpPage.mobileNumberMessage')}</Text>
          <YStack>
            <SchemaForm
              form={form}
              formProps={{ disabled: isSubmitting }}
              props={{
                mobileNumber: {
                  autoFocus: true,
                },
              }}
              onSubmit={onSubmit}
              schema={MobileNumberFormSchema}
              defaultValues={{
                mobileNumber: savedMobileNumber,
              }}
            />
          </YStack>
          <YStack alignItems="center" gap="$lg" flexDirection="row">
            <Button
              onPress={() => {
                void form.handleSubmit(onSubmit)();
              }}
              mode="primary"
              variant="filled"
              size="lg"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {t('common.buttons.continue')}
            </Button>
          </YStack>
        </YStack>
      )}
    </YStack>
  );
}

export function MobileNumberStep() {
  const setActiveStep = useSetActiveStep();
  const [showMFAForm, setShowMFAForm] = useState<boolean>(false);

  return (
    <PageBackNavigationFrame
      onPressBack={() => {
        if (showMFAForm) {
          setShowMFAForm(false);
        } else {
          setActiveStep('email');
        }
      }}
    >
      <MobileNumberForm showMFAForm={showMFAForm} setShowMFAForm={setShowMFAForm} />
    </PageBackNavigationFrame>
  );
}

export const MobileNumberStepWithAnalytics = withTrackScreenView(MobileNumberStep, {
  pageName: 'MobileNumber',
});
